import { Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import ScrollToTop from '@/components/base/ScrollToTop';
import { siteInfo } from '@/mocks/site';
import { categories } from '@/mocks/categories';
import { featuredArticles, articles } from '@/mocks/articles';

export default function Home() {
  return (
    <>
      <ScrollToTop />
      <SEOHead
        title="首页"
        description={siteInfo.description}
        keywords={siteInfo.keywords}
        canonicalUrl={siteInfo.url}
        ogImage="https://readdy.ai/api/search-image?query=Cinematic%20movie%20poster%20style%20with%20warm%20golden%20and%20amber%20tones%2C%20classic%20Hong%20Kong%20cinema%20aesthetic%2C%20nostalgic%201990s%20film%20atmosphere%2C%20elegant%20typography%2C%20soft%20lighting%2C%20vintage%20film%20texture%2C%20artistic%20composition&width=1200&height=630&seq=home-og-01&orientation=landscape"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: siteInfo.name,
          url: siteInfo.url,
          description: siteInfo.description,
          potentialAction: {
            '@type': 'SearchAction',
            target: `${siteInfo.url}/search?q={search_term_string}`,
            'query-input': 'required name=search_term_string',
          },
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <section className="relative h-[520px] md:h-[620px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Cinematic%20film%20reel%20and%20warm%20golden%20spotlight%20on%20dark%20stage%2C%20classic%20Hong%20Kong%20cinema%20nostalgic%20atmosphere%2C%20soft%20dramatic%20lighting%2C%20movie%20theater%20curtain%20texture%2C%20vintage%20film%20aesthetic%2C%20elegant%20amber%20tones%2C%20artistic%20photography%2C%20no%20text%2C%20cinematic%20composition&width=1600&height=900&seq=hero-bg-01&orientation=landscape"
              alt="蜜桃成熟时电影经典场景"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/60"></div>
          </div>
          <div className="relative z-10 text-center px-4 w-full max-w-3xl mx-auto">
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight">
              {siteInfo.name}
            </h1>
            <p className="text-base md:text-lg text-white/80 mb-8 max-w-xl mx-auto leading-relaxed">
              {siteInfo.description}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/category/plot-analysis"
                className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-6 py-3 rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-film-line"></i>
                浏览剧情解析
              </Link>
              <Link
                to="/category/cast-intro"
                className="inline-flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white border border-white/40 px-6 py-3 rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-user-star-line"></i>
                查看演员阵容
              </Link>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950">探索内容分类</h2>
              <p className="mt-3 text-foreground-600 text-sm">按分类浏览蜜桃成熟时的全方位资讯</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group bg-background-50 border border-background-200/70 rounded-xl p-6 text-center hover:border-amber-400/50 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-amber-50 flex items-center justify-center">
                    <i className={`${cat.icon} text-xl text-amber-600`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-950 mb-2 whitespace-nowrap">{cat.name}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{cat.description}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-16 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950">精选文章</h2>
              <p className="mt-3 text-foreground-600 text-sm">编辑精选的深度好文，助您全面了解这部经典港片</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredArticles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-amber-400/50 transition-all duration-300 cursor-pointer"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-xs text-amber-600 font-medium bg-amber-50 px-2 py-0.5 rounded-full">
                        {categories.find((c) => c.slug === article.category)?.name}
                      </span>
                      <span className="text-xs text-foreground-400">{article.readTime}</span>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-950 mb-2 line-clamp-2 leading-snug">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-500 line-clamp-2 leading-relaxed">{article.excerpt}</p>
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-background-200/70">
                      <span className="text-xs text-foreground-400">{article.date}</span>
                      <span className="text-xs text-amber-600 flex items-center gap-1 group-hover:gap-2 transition-all">
                        阅读全文 <i className="ri-arrow-right-line text-xs"></i>
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950">最新文章</h2>
              <p className="mt-3 text-foreground-600 text-sm">持续更新，为您带来蜜桃成熟时相关的最新深度内容</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {articles.filter((a) => !a.featured).slice(0, 7).map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group flex gap-4 p-4 bg-background-50 border border-background-200/70 rounded-xl hover:border-amber-400/50 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-24 h-24 md:w-28 md:h-28 flex-shrink-0 rounded-lg overflow-hidden">
                    <img
                      src={article.thumbUrl}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs text-amber-600 font-medium bg-amber-50 px-2 py-0.5 rounded-full whitespace-nowrap">
                        {categories.find((c) => c.slug === article.category)?.name}
                      </span>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-950 line-clamp-2 leading-snug mb-1">
                      {article.title}
                    </h3>
                    <p className="text-xs text-foreground-400 line-clamp-1">{article.excerpt}</p>
                    <div className="flex items-center gap-3 mt-2">
                      <span className="text-xs text-foreground-400">{article.date}</span>
                      <span className="text-xs text-foreground-400">{article.readTime}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link
                to="/category/plot-analysis"
                className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-6 py-3 rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                查看全部文章 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-16 bg-background-100">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950">常见问题</h2>
              <p className="mt-3 text-foreground-600 text-sm">关于蜜桃成熟时电影的常见问题解答</p>
            </div>
            <div className="space-y-3" itemScope itemType="https://schema.org/FAQPage">
              <FAQItem
                question="蜜桃成熟时是哪一年上映的电影？"
                answer="《蜜桃成熟时》于1993年在香港上映，由张肇麟执导，李丽珍主演。这部电影是90年代香港情色喜剧电影中的经典代表作，在当时的香港电影市场引起了广泛关注。"
              />
              <FAQItem
                question="蜜桃成熟时的女主角是谁？"
                answer="蜜桃成熟时的女主角是李丽珍。她1966年出生于香港，凭借此片成功从清纯玉女转型为性感女神，成为90年代香港影坛最具代表性的女演员之一。李丽珍在片中的精彩演绎至今仍被影迷津津乐道。"
              />
              <FAQItem
                question="蜜桃成熟时的导演是谁？"
                answer="《蜜桃成熟时》由香港导演张肇麟执导。张肇麟是90年代香港影坛的重要导演之一，擅长将情色元素与喜剧风格相结合，创作出既有市场吸引力又不失艺术追求的独特作品。"
              />
              <FAQItem
                question="蜜桃成熟时属于什么类型的电影？"
                answer="《蜜桃成熟时》属于香港情色喜剧电影（香港三级片）。它将情欲元素与轻松幽默的喜剧风格巧妙融合，在情色电影类型中具有里程碑式的意义，展现了90年代香港独特的电影文化。"
              />
              <FAQItem
                question="在哪里可以看到蜜桃成熟时的完整版？"
                answer="《蜜桃成熟时》作为经典港片，目前可以在部分线上影视平台找到资源。由于年代久远，建议通过正版渠道观看。本站提供的是电影相关的文化研究、剧情分析和幕后故事等内容，不提供任何盗版资源。"
              />
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
      <button
        className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
        onClick={() => setOpen(!open)}
      >
        <span className="text-sm font-medium text-foreground-950 pr-4" itemProp="name">{question}</span>
        <i className={`text-foreground-400 transition-transform duration-200 ${open ? 'ri-subtract-line' : 'ri-add-line'}`}></i>
      </button>
      {open && (
        <div className="px-5 pb-4" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
          <p className="text-sm text-foreground-600 leading-relaxed" itemProp="text">{answer}</p>
        </div>
      )}
    </div>
  );
}